import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api, setAuthToken, clearAuthToken } from '../api/client';

interface User {
  userId: string;
  username: string;
  name: string;
  level: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ACCESS_TOKEN_KEY = 'modoostay_access_token';
const REFRESH_TOKEN_KEY = 'modoostay_refresh_token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing token on mount
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      setAuthToken(token);
      fetchCurrentUser();
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.data);
    } catch (error) {
      // Token might be expired, try to refresh
      await refreshToken();
    } finally {
      setIsLoading(false);
    }
  };

  const refreshToken = async () => {
    const refresh = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refresh) {
      clearTokens();
      return;
    }

    try {
      const response = await api.post('/auth/refresh', { refreshToken: refresh });
      const { accessToken } = response.data.data;
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      setAuthToken(accessToken);
      await fetchCurrentUser();
    } catch (error) {
      clearTokens();
    }
  };

  const clearTokens = () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    clearAuthToken();
    setUser(null);
  };

  const login = async (username: string, password: string) => {
    const response = await api.post('/auth/login', { username, password });
    const { accessToken, refreshToken, user: userData } = response.data.data;

    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    setAuthToken(accessToken);
    setUser(userData);
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    try {
      await api.post('/auth/logout', { refreshToken });
    } catch (error) {
      // Ignore errors during logout
    }
    clearTokens();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
